import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '@/lib/api-client';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Lock, ArrowRight, Loader2, CheckCircle } from 'lucide-react';

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleReset = async () => {
    if (!password || password.length < 8) {
      setError(t('misc_login.password_min'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('misc_login.password_mismatch'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { error: resetError } = await apiClient.POST('/reset-password', {
        body: {
          token: token ?? '',
          email: email ?? '',
          password,
          password_confirmation: confirmPassword,
        },
      });

      if (resetError) {
        setError(t('misc_reset.link_invalid_retry'));
        return;
      }

      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm animate-fade-in">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-foreground tracking-tight">Hisob POS</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('misc_reset.title')}</p>
        </div>

        {success ? (
          <div className="p-4 rounded-xl border bg-success/10 border-success/20 text-center space-y-2">
            <CheckCircle size={32} className="mx-auto text-success" />
            <p className="text-sm text-foreground font-medium">{t('misc_reset.success')}</p>
            <p className="text-xs text-muted-foreground">{t('misc_reset.redirecting')}</p>
          </div>
        ) : !token || !email ? (
          <div className="p-4 rounded-xl border bg-warning/10 border-warning/20 text-center space-y-2">
            <p className="text-3xl">⏳</p>
            <p className="text-sm text-foreground font-medium">{t('misc_reset.link_invalid')}</p>
            <p className="text-xs text-muted-foreground">{t('misc_reset.link_hint')}</p>
            <Button variant="outline" size="sm" onClick={() => navigate('/login')}>
              {t('misc_reset.back_login')}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">{t('misc_reset.new_password')}</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="password"
                  placeholder={t('misc_login.password_min_ph')}
                  value={password}
                  onChange={e => { setPassword(e.target.value); setError(''); }}
                  className="h-11 pl-10"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">{t('misc_login.password_confirm')}</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="password"
                  placeholder={t('misc_login.password_confirm_ph')}
                  value={confirmPassword}
                  onChange={e => { setConfirmPassword(e.target.value); setError(''); }}
                  className="h-11 pl-10"
                />
              </div>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button onClick={handleReset} disabled={loading} className="w-full h-11">
              {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : <ArrowRight size={16} className="mr-2" />}
              {t('misc_reset.submit')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
